import { Input } from '@shared/components/ui/input';
import { useTranslation } from 'react-i18next';
import { OwnBoxPackaging } from '../../models/rate.types';
import { formatCurrencyAmount, getCurrencySymbol, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';

interface PackagingPricesTableProps {
  ownBoxes: OwnBoxPackaging[];
  prices: Record<number, number>;
  onPriceChange: (packagingId: number, value: string) => void;
  errors?: Record<number, string>;
  readOnly?: boolean;
}

export const PackagingPricesTable = ({
  ownBoxes,
  prices,
  onPriceChange,
  errors,
  readOnly = false,
}: PackagingPricesTableProps) => {
  const { t } = useTranslation('ratesModule');
  const currency = useCurrency();
  const currencySymbol = getCurrencySymbol(currency);

  if (ownBoxes.length === 0) {
    return (
      <p className="text-sm text-subtext py-4 text-center" data-testid="packaging-prices-empty">
        {t('sections.packagingPrices.empty')}
      </p>
    );
  }

  return (
    <div className="border border-card-border rounded-lg overflow-hidden" data-testid="packaging-prices-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-card-border/30 border-b border-card-border">
            <th className="text-left px-4 py-2 font-medium text-subtext">
              {t('sections.packagingPrices.columns.name')}
            </th>
            <th className="text-left px-4 py-2 font-medium text-subtext">
              {t('sections.packagingPrices.columns.sku')}
            </th>
            <th className="text-right px-4 py-2 font-medium text-subtext">
              {t('sections.packagingPrices.columns.ratePrice', { currency })}
            </th>
          </tr>
        </thead>
        <tbody>
          {ownBoxes.map((box) => (
            <tr key={box.id} className="border-b border-card-border last:border-b-0">
              <td className="px-4 py-3 text-title">{box.name}</td>
              <td className="px-4 py-3 text-subtext">{box.code ?? '—'}</td>
              <td className="px-4 py-3 text-right">
                {readOnly ? (
                  <span className="font-medium text-title">{formatCurrencyAmount(prices[box.id] ?? 0, currency)}</span>
                ) : (
                  <div className="flex flex-col items-end gap-1">
                    <div className="relative inline-flex items-center">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={prices[box.id] ?? ''}
                        onChange={(e) => onPriceChange(box.id, e.target.value)}
                        className={`w-28 text-right pr-7 ${errors?.[box.id] ? 'border-error' : ''}`}
                        data-testid={`packaging-price-input-${box.id}`}
                      />
                      <span className="absolute right-2 text-xs text-subtext pointer-events-none">
                        {currencySymbol}
                      </span>
                    </div>
                    {errors?.[box.id] && <span className="text-xs text-error">{errors[box.id]}</span>}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
